// components/SystemSelectorModal.tsx
import React, { useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  MenuItem,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BWITextField from './shared/BWITextField';
import { ApiSystem, System } from '../state/types';
import { useSession } from '../context/SessionContext';
import { groupAndFilterSystems } from './system/systemHelpers';
import { userHasAccess } from '../services/RoleBasedAccess';

interface Props {
  open: boolean;
  onClose: () => void;
}

const SystemSelectorModal: React.FC<Props> = ({
  open,
  onClose,
}) => {

  const { systems, selectedSystem, selectSystem } = useSession();

  const [search, setSearch] = React.useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [open]);

  const accessibleCodes = systems.map(s => s.systemCode)

  const filteredGroups = groupAndFilterSystems(systems, accessibleCodes, search);

  const handleSelectSystem = (sysCode: string) => {
    const system = systems.filter((system: ApiSystem) => system.systemCode === sysCode)[0]
    selectSystem(system)
  }

  if (!systems || systems.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Select a System</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Loading systems...
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      // 1. Connects the modal to its title for screen readers
      aria-labelledby="system-selector-title"
    >
      <DialogTitle id="system-selector-title">Select a System</DialogTitle>
      <DialogContent dividers>
        <BWITextField
          startIcon={<SearchIcon />}
          placeholder="Search Systems..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          inputRef={searchInputRef}
          fullWidth
          sx={{ mb: 2 }}
          inputProps={{
            'aria-label': 'Search available systems',
          }}
        />
        {filteredGroups.map((group) => (
          <Box
            key={group.label}
            sx={{ mb: 2 }}
            role="group" // 3. Identifies this as a logical group
            aria-label={`${group.label} systems`}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }} aria-hidden="true">
              {group.label}
            </Typography>
            {group.systems.map(({ code, label }) => {
              const isSelected = code === selectedSystem?.systemCode;
              return (
                <MenuItem
                  key={code}
                  selected={isSelected}
                  onClick={() => {
                    handleSelectSystem(code);
                    onClose();
                  }}
                  // 4. Describes the specific action of this list item
                  aria-label={`Select system ${label}`}
                  // 5. Tells the reader if this item is the one currently active
                  aria-selected={isSelected}
                >
                  <Typography variant="body1">{label}</Typography>
                </MenuItem>
              );
            })}
          </Box>
        ))}
        {filteredGroups.length === 0 && (
          <Typography variant="body2" color="text.secondary" role="alert">
            No systems match your search.
          </Typography>
        )}

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} aria-label="Close system selector">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SystemSelectorModal;
